// Session seam for forge-auth (Better Auth, cookie sessions).
//
// The game is offline-first: single-player never needs an account. Auth is
// purely additive, so everything here degrades to "signed out" — no auth URL
// configured, network down, CORS refused, non-2xx, malformed JSON — and
// never throws into the UI.
//
// Configure with VITE_AUTH_URL (e.g. https://forge-auth.blueforge.studio).
// forge-auth must list the game's origin in its trusted origins for the
// credentialed cross-origin get-session call to succeed.

export interface SessionUser {
  readonly id: string;
  readonly name: string | null;
  readonly email: string | null;
  readonly image: string | null;
}

export type SessionState =
  | { readonly status: 'loading' }
  | { readonly status: 'signed-out' }
  | { readonly status: 'signed-in'; readonly user: SessionUser };

type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

/** Normalised auth origin (no trailing slash), or null when auth is not configured. */
export function resolveAuthBaseUrl(raw: string | undefined | null): string | null {
  const value = raw?.trim();
  if (!value) return null;
  try {
    const url = new URL(value);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    return url.href.replace(/\/+$/, '');
  } catch {
    return null;
  }
}

export function getAuthBaseUrl(): string | null {
  return resolveAuthBaseUrl(import.meta.env?.VITE_AUTH_URL);
}

function str(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/** Parse Better Auth's `{ session, user } | null` body. Anything unexpected → null. */
export function parseSessionUser(body: unknown): SessionUser | null {
  if (!body || typeof body !== 'object') return null;
  const { session, user } = body as { session?: unknown; user?: unknown };
  if (!session || !user || typeof user !== 'object') return null;
  const u = user as Record<string, unknown>;
  const id = str(u.id);
  if (!id) return null;
  return { id, name: str(u.name), email: str(u.email), image: str(u.image) };
}

/**
 * Fetch the current session. Resolves to the signed-in user, or null for
 * every failure mode (including "auth not configured").
 */
export async function fetchSessionUser(
  baseUrl: string | null,
  fetchImpl: FetchLike | undefined = globalThis.fetch,
): Promise<SessionUser | null> {
  if (!baseUrl || typeof fetchImpl !== 'function') return null;
  try {
    const res = await fetchImpl(`${baseUrl}/api/auth/get-session`, {
      credentials: 'include',
      headers: { accept: 'application/json' },
    });
    if (!res.ok) return null;
    return parseSessionUser(await res.json());
  } catch {
    return null;
  }
}

/** forge-auth hosted login page; it sends the player back to `returnTo` afterwards. */
export function signInUrl(baseUrl: string, returnTo: string): string {
  return `${baseUrl}/login?redirect=${encodeURIComponent(returnTo)}`;
}

/** Best-effort sign-out. Resolves true when the server acknowledged it. */
export async function postSignOut(
  baseUrl: string | null,
  fetchImpl: FetchLike | undefined = globalThis.fetch,
): Promise<boolean> {
  if (!baseUrl || typeof fetchImpl !== 'function') return false;
  try {
    const res = await fetchImpl(`${baseUrl}/api/auth/sign-out`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: '{}',
    });
    return res.ok;
  } catch {
    return false;
  }
}

/** Up to two initials for the avatar fallback ("Ada Lovelace" → "AL"). */
export function initialsFor(user: Pick<SessionUser, 'name' | 'email'>): string {
  const source = user.name ?? user.email?.split('@')[0] ?? '';
  const parts = source.split(/[\s._-]+/).filter(Boolean);
  const letters = parts.length >= 2 ? `${parts[0]?.[0]}${parts[1]?.[0]}` : source.slice(0, 2);
  return letters.toUpperCase() || '?';
}
